import type { AudioEngine } from "../core/AudioEngine";

/** The browser surface the lifecycle listens to (the globals by default; fakes in tests). */
export interface AudioLifecycleHost {
  document?: { addEventListener(type: "visibilitychange", listener: () => void): void; readonly hidden: boolean } | undefined;
  window?:
    | {
        addEventListener(type: "pagehide" | "pageshow" | "focus" | GestureEvent, listener: () => void, options?: AddEventListenerOptions): void;
        removeEventListener(type: GestureEvent, listener: () => void, options?: EventListenerOptions): void;
      }
    | undefined;
}

/** The inputs that give a page user activation, the browsers' condition for starting an AudioContext. */
const GESTURE_EVENTS = ["pointerdown", "pointerup", "touchend", "keydown", "click"] as const;
type GestureEvent = (typeof GESTURE_EVENTS)[number];

/** What the service does at each lifecycle moment; the lifecycle only decides WHEN. */
export interface AudioLifecycleReactions {
  /**
   * `pagehide`: hard-stop the music and ambient beds, the intro and the big-win count, synchronously
   * (deferred work may never run). The keyed loops (the reel roll) are left to their owner: a page
   * restored mid-spin resumes the spin, and its roll with it (review finding 70).
   */
  teardown(): void;
  /**
   * Back in the foreground after a hide. `fresh` = the current loops must be recreated: iOS Safari
   * may have killed the sources during a long background, or a prior `pagehide` already stopped
   * them. The reaction resumes the engine and puts back the music and the ambient bed the game last
   * asked for: recreated when `fresh`; otherwise they carry on from where the hide paused them, and
   * only a bed that is not what was asked for is started.
   */
  resume(fresh: boolean): Promise<void>;
  /**
   * A user gesture re-unlocked the engine after a hide: put back the music and the ambient bed the
   * game last asked for where what plays is not it (a start the engine refused while it was not running).
   */
  reactivated(): void;
}

const LONG_BACKGROUND_MS = 10_000;

/**
 * The audio's browser lifecycle: page hide / show, `pagehide` teardown, long-background detection,
 * the user-gesture unlock and the reactivation after a hide. One flag set coalesces the multi-fire
 * browser events (`visibilitychange`, `pageshow` and `focus` can all fire on a single restore;
 * without it each would trigger resume work and the overlap creates the "music plays while another
 * tab is open" race). `pagehide` also marks hidden so the following `visibilitychange` is a no-op.
 */
export class AudioLifecycle {
  private bound = false;
  private needsUserGestureReactivation = false;
  private hidden = false;
  private hiddenAtMs: number | null = null;
  /** `teardown` hard-stopped the loops: the next foreground must recreate them. */
  private loopsHardStopped = false;
  /** Removes the page-wide gesture listeners `armGestureUnlock` added; null while none are armed. */
  private disarmGestureUnlock: (() => void) | null = null;

  public constructor(
    private readonly deps: {
      engine: AudioEngine;
      reactions: AudioLifecycleReactions;
      host?: AudioLifecycleHost;
      now?: () => number;
      longBackgroundMs?: number;
    },
  ) {}

  /** Listen to the browser; idempotent. Bound after the first unlock (before it there is nothing to suspend). */
  public bind(): void {
    if (this.bound) return;
    const host = this.host();
    host.document?.addEventListener("visibilitychange", () => {
      if (host.document?.hidden) this.handleHide();
      else this.handleShow();
    });
    host.window?.addEventListener("pagehide", () => this.teardownForPageHide());
    host.window?.addEventListener("pageshow", () => this.handleShow());
    host.window?.addEventListener("focus", () => this.handleShow());
    this.bound = true;
  }

  /** The first (or a renewed) user gesture: unlock the engine; true when it is running. */
  public async unlockFromUserGesture(): Promise<boolean> {
    const unlocked = await this.deps.engine.unlockFromUserGesture();
    if (unlocked) {
      this.needsUserGestureReactivation = false;
      this.disarmGestureUnlock?.();
      this.disarmGestureUnlock = null;
    }
    return unlocked;
  }

  /**
   * Until the engine runs, every gesture anywhere on the page retries the unlock, inside that
   * gesture's handler as the browsers require. An unlock without a gesture behind it (a boot with
   * no intro splash) is refused unless the page already had user activation, and nothing else would
   * try again. `onUnlocked` may be called more than once (one tap fires several events); the first
   * successful unlock removes the listeners. Idempotent while armed.
   */
  public armGestureUnlock(onUnlocked: () => void): void {
    if (this.disarmGestureUnlock) return;
    const win = this.host().window;
    if (!win) return;
    const onGesture = (): void => {
      void this.unlockFromUserGesture().then((unlocked) => {
        if (unlocked) onUnlocked();
      });
    };
    for (const type of GESTURE_EVENTS) win.addEventListener(type, onGesture, { capture: true });
    this.disarmGestureUnlock = () => {
      for (const type of GESTURE_EVENTS) win.removeEventListener(type, onGesture, { capture: true });
    };
  }

  /** Called on every emit / play: after a hide, the next gesture re-unlocks the engine and the beds are put back (`reactivated`). */
  public reactivateIfNeededFromGesture(): void {
    if (!this.needsUserGestureReactivation) return;
    void (async () => {
      const unlocked = await this.deps.engine.unlockFromUserGesture();
      if (!unlocked) return;
      this.needsUserGestureReactivation = false;
      this.deps.reactions.reactivated();
    })();
  }

  private handleHide(): void {
    if (this.hidden) return;
    this.hidden = true;
    this.hiddenAtMs = this.now();
    this.needsUserGestureReactivation = true;
    const ctx = this.deps.engine.audioContext;
    if (ctx && ctx.state === "running") {
      void ctx.suspend().catch(() => {});
    }
  }

  private handleShow(): void {
    if (!this.hidden) return;
    this.hidden = false;
    this.needsUserGestureReactivation = true;
    const longBackground = this.hiddenAtMs !== null && this.now() - this.hiddenAtMs > (this.deps.longBackgroundMs ?? LONG_BACKGROUND_MS);
    this.hiddenAtMs = null;
    const fresh = longBackground || this.loopsHardStopped;
    this.loopsHardStopped = false;
    void this.deps.reactions.resume(fresh);
  }

  /** Hard-stop all output before the page leaves or is parked in the BFCache; all synchronous. */
  private teardownForPageHide(): void {
    this.deps.reactions.teardown();
    this.loopsHardStopped = true;
    this.hidden = true;
    const ctx = this.deps.engine.audioContext;
    if (ctx && ctx.state !== "suspended" && ctx.state !== "closed") {
      void ctx.suspend().catch(() => {});
    }
    this.needsUserGestureReactivation = true;
  }

  private host(): AudioLifecycleHost {
    return this.deps.host ?? { document: typeof document !== "undefined" ? document : undefined, window: typeof window !== "undefined" ? window : undefined };
  }

  private now(): number {
    return (this.deps.now ?? (() => performance.now()))();
  }
}
